<script lang="ts">
	/**
	 * `/Settings/Admin/Settings/Refunds` — how long a customer may refund themselves (§9.6).
	 *
	 * A purchase's window is read **product → billing country → currency → store default**, and the
	 * page says so before anything else, because four places that can each set one number are four
	 * places an admin can look at and still not know which is in force.
	 *
	 * The country comes before the currency on purpose. A withdrawal right is the law of the buyer's
	 * country, and currency is a poor proxy for it: euros are spent outside the EU, much of the EU
	 * bills in its own currency, and EU and UK buyers routinely pay in dollars. The currency row is
	 * still useful for what a country row cannot say cheaply — "every dollar sale gets thirty days".
	 *
	 * The statutory floors are stated beside the country table rather than enforced: they are the
	 * store's obligation to know, a store may well be *more* generous, and a refusal here would block
	 * an admin entering the law of a country this list has fallen behind on.
	 */

	import { onMount } from 'svelte';
	import { Button, Input, Table, TableBody, TableBodyCell, TableBodyRow, TableHead, TableHeadCell } from 'flowbite-svelte';
	import { ConfirmButton, CountrySelect, EmptyState, FormField, PageHeader, Panel, RoutedLink } from '../../components';
	import {
		clearCountryRefundWindow,
		getRefundPolicy,
		setCountryRefundWindow,
		setCurrencyRefundWindow
	} from '../../AppNetwork';
	import { countryName } from '../../Countries';
	import type { RefundPolicy } from '../../AppTypes';
	import { toastError, toastSuccess } from '../../stores/toast';

	/** Statutory minimums for a consumer, where one exists. A floor, never a ceiling. */
	const STATUTORY_FLOORS: Record<string, number> = {
		BR: 7,
		KR: 7,
		GB: 14,
		TR: 14,
		...Object.fromEntries(
			['AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE', 'IS', 'LI', 'NO'].map(code => [code, 14])
		)
	};

	let policy: RefundPolicy | null = null;
	let loading: boolean = true;
	let loadError: string = '';
	let busy: string = '';

	/** Text, because an empty field means "no window of its own" and a number input reads it as 0. */
	let currencyDrafts: Record<string, string> = {};
	let countryDrafts: Record<string, string> = {};

	let newCountry: string = '';
	let newDays: string = '';

	function fnParse(text: string): number | null {
		const trimmed = text.trim();
		if (!/^\d{1,3}$/.test(trimmed)) return null;
		const days = Number(trimmed);
		return days <= 365 ? days : null;
	}

	function fnAdopt(next: RefundPolicy): void {
		policy = next;
		currencyDrafts = Object.fromEntries(next.currencies.map(row => [row.code, row.refundWindowDays === null ? '' : String(row.refundWindowDays)]));
		countryDrafts = Object.fromEntries(next.countries.map(row => [row.country, String(row.refundWindowDays)]));
	}

	async function fnLoad(): Promise<void> {
		loading = true;
		loadError = '';
		try {
			fnAdopt(await getRefundPolicy());
		} catch (error) {
			loadError = (error as Error).message;
		} finally {
			loading = false;
		}
	}

	async function fnRun(key: string, work: () => Promise<RefundPolicy>, done: string): Promise<void> {
		busy = key;
		try {
			fnAdopt(await work());
			toastSuccess(done);
		} catch (error) {
			toastError('The refund policy was not saved.', (error as Error).message);
		} finally {
			busy = '';
		}
	}

	function fnFloorNote(country: string, days: number | null): string {
		const floor = STATUTORY_FLOORS[country];
		if (floor === undefined || days === null || days >= floor) return '';
		return `Below the ${floor}-day statutory minimum for consumers in ${countryName(country)}.`;
	}

	$: newDaysParsed = fnParse(newDays);
	$: newCountryTaken = policy?.countries.some(row => row.country === newCountry) ?? false;
	$: canAdd = busy === '' && newCountry !== '' && newDaysParsed !== null && !newCountryTaken;

	async function fnAdd(): Promise<void> {
		if (newDaysParsed === null) return;
		const country = newCountry;
		await fnRun(`country:${country}`, () => setCountryRefundWindow(country, newDaysParsed as number), `${countryName(country)} saved.`);
		newCountry = '';
		newDays = '';
	}

	onMount(fnLoad);
</script>

<PageHeader title="Refund policy">
	<svelte:fragment slot="description">
		How long after paying a customer can refund themselves, by where they bill from and what they pay in.
	</svelte:fragment>
</PageHeader>

{#if loading}
	<EmptyState variant="loading" title="Loading the refund policy…" />
{:else if loadError.length > 0}
	<EmptyState variant="error" title="The refund policy could not be loaded." description={loadError}>
		<Button color="alternative" size="sm" on:click={fnLoad}>Try again</Button>
	</EmptyState>
{:else if policy}
	<div class="space-y-6">
		<Panel title="Which window applies" faIcon="list-ol">
			<ol class="list-decimal ms-5 space-y-1 text-sm">
				<li><strong>The product's own window</strong>, set on the product in the catalogue. Zero there makes a product support-only everywhere.</li>
				<li><strong>The customer's billing country</strong>, below. This is the one consumer law follows.</li>
				<li><strong>The currency they paid in</strong>, below.</li>
				<li>
					<strong>The store default: {policy.defaultWindowDays} days</strong>, from
					<RoutedLink to="/Settings/Admin/Settings" class="text-primary-600 dark:text-primary-400 hover:underline">billing.refundWindowDays</RoutedLink>.
				</li>
			</ol>
			<p class="text-sm text-default mt-3">
				Zero at any level turns self-serve refunds off for those purchases; the customer is pointed at support instead.
			</p>
		</Panel>

		<Panel title="By billing country" faIcon="earth-europe">
			<p class="text-sm text-default mb-4">
				Consumers in the EU, the UK and Turkey have at least 14 days to withdraw from an online purchase, and in Brazil and
				South Korea at least 7. A shorter window here is flagged but not refused — being more generous than the law is always allowed.
			</p>

			{#if policy.countries.length === 0}
				<EmptyState compact title="No country has a window of its own." description="Every customer falls through to their currency, then the store default." />
			{:else}
				<Table divClass="relative overflow-x-auto mb-4">
					<TableHead>
						<TableHeadCell>Country</TableHeadCell>
						<TableHeadCell>Days</TableHeadCell>
						<TableHeadCell />
					</TableHead>
					<TableBody>
						{#each policy.countries as row (row.country)}
							{@const parsed = fnParse(countryDrafts[row.country] ?? '')}
							<TableBodyRow>
								<TableBodyCell class="dataTable">
									<div>{countryName(row.country)}</div>
									<div class="text-xs text-default">{row.country}</div>
								</TableBodyCell>
								<TableBodyCell class="dataTable">
									<Input
										size="sm"
										class="w-24"
										inputmode="numeric"
										aria-label="Days for {countryName(row.country)}"
										bind:value={countryDrafts[row.country]}
										disabled={busy !== ''}
									/>
									{#if fnFloorNote(row.country, parsed)}
										<p class="text-xs text-amber-700 dark:text-amber-400 mt-1">{fnFloorNote(row.country, parsed)}</p>
									{/if}
								</TableBodyCell>
								<TableBodyCell class="dataTable">
									<div class="flex gap-2 justify-end">
										<Button
											size="xs"
											color="alternative"
											disabled={busy !== '' || parsed === null || parsed === row.refundWindowDays}
											on:click={() =>
												fnRun(`country:${row.country}`, () => setCountryRefundWindow(row.country, parsed ?? 0), `${countryName(row.country)} saved.`)}
										>
											Save
										</Button>
										<ConfirmButton
											size="xs"
											color="alternative"
											disabled={busy !== ''}
											confirmText={`Remove ${countryName(row.country)}'s window? Its customers fall through to their currency, then the store default.`}
											on:confirm={() =>
												fnRun(`country:${row.country}`, () => clearCountryRefundWindow(row.country), `${countryName(row.country)} removed.`)}
										>
											Remove
										</ConfirmButton>
									</div>
								</TableBodyCell>
							</TableBodyRow>
						{/each}
					</TableBody>
				</Table>
			{/if}

			<div class="grid gap-3 sm:grid-cols-[1fr_8rem_auto] items-end">
				<FormField id="refund-country" label="Add a country" error={newCountryTaken ? 'That country already has a window — edit it above.' : ''}>
					<CountrySelect id="refund-country" bind:value={newCountry} disabled={busy !== ''} />
				</FormField>
				<FormField id="refund-days" label="Days">
					<Input id="refund-days" inputmode="numeric" placeholder="14" bind:value={newDays} disabled={busy !== ''} />
				</FormField>
				<Button disabled={!canAdd} on:click={fnAdd}>Add</Button>
			</div>
			{#if newCountry !== '' && fnFloorNote(newCountry, newDaysParsed)}
				<p class="text-xs text-amber-700 dark:text-amber-400 mt-2">{fnFloorNote(newCountry, newDaysParsed)}</p>
			{/if}
		</Panel>

		<Panel title="By currency" faIcon="coins">
			<p class="text-sm text-default mb-4">Used when the customer's country has no window of its own. Leave blank for the store default.</p>
			<Table divClass="relative overflow-x-auto">
				<TableHead>
					<TableHeadCell>Currency</TableHeadCell>
					<TableHeadCell>Days</TableHeadCell>
					<TableHeadCell />
				</TableHead>
				<TableBody>
					{#each policy.currencies as row (row.code)}
						{@const text = (currencyDrafts[row.code] ?? '').trim()}
						{@const parsed = text === '' ? null : fnParse(text)}
						{@const invalid = text !== '' && parsed === null}
						<TableBodyRow>
							<TableBodyCell class="dataTable">
								<div>{row.symbol} {row.code.toUpperCase()}</div>
								<div class="text-xs text-default">{row.name}{row.isEnabled ? '' : ' · disabled'}</div>
							</TableBodyCell>
							<TableBodyCell class="dataTable">
								<Input
									size="sm"
									class="w-24"
									inputmode="numeric"
									placeholder={String(policy.defaultWindowDays)}
									aria-label="Days for {row.code.toUpperCase()}"
									aria-invalid={invalid}
									bind:value={currencyDrafts[row.code]}
									disabled={busy !== ''}
								/>
								{#if invalid}
									<p class="text-xs text-red-600 dark:text-red-400 mt-1">A whole number of days, 0 to 365.</p>
								{/if}
							</TableBodyCell>
							<TableBodyCell class="dataTable">
								<div class="flex justify-end">
									<Button
										size="xs"
										color="alternative"
										disabled={busy !== '' || invalid || parsed === row.refundWindowDays}
										on:click={() =>
											fnRun(`currency:${row.code}`, () => setCurrencyRefundWindow(row.code, parsed), `${row.code.toUpperCase()} saved.`)}
									>
										Save
									</Button>
								</div>
							</TableBodyCell>
						</TableBodyRow>
					{/each}
				</TableBody>
			</Table>
		</Panel>
	</div>
{/if}
